import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Box,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { formatNumber } from "../../../utils/formatNumber";

const numberValue = (value) => Number(value ?? 0) || 0;

export default function InsurancePriceDetailModal({
  open,
  onClose,
  item,
  lockItems = [],
  onSave,
}) {
  const [mode, setMode] = useState("base");
  const [customPrice, setCustomPrice] = useState("");
  const [openExtraPriceDialog, setOpenExtraPriceDialog] = useState(false);

  // เปิดรายละเอียดอีกครั้งแล้วคงราคาส่วนเกินที่บันทึกไว้เดิม
  useEffect(() => {
    const persistedPrice = item?.originalItem?.insuranceExtraPrice;
    const persistedMode = item?.originalItem?.insuranceExtraPriceMode;
    if (persistedPrice != null) {
      const nextMode = persistedMode || "custom";
      setMode(nextMode);
      setCustomPrice(
        nextMode === "custom" ? String(numberValue(persistedPrice)) : "",
      );
      return;
    }

    const savedExtra = item?.details?.find(
      (detail) =>
        !detail?.isLockedPrice &&
        detail?.priceSource !== "lock" &&
        detail?.priceSource !== "po" &&
        numberValue(detail?.usedWeight) > 0,
    );
    if (!savedExtra) {
      setMode("base");
      setCustomPrice("");
      return;
    }

    if (savedExtra.priceSource === "group" || savedExtra.priceSource === "base") {
      setMode(savedExtra.priceSource);
      setCustomPrice("");
    } else {
      setMode("custom");
      setCustomPrice(String(numberValue(savedExtra.pricePerUnit)));
    }
  }, [item]);

  const details = useMemo(() => {
    const originalNetWeight = numberValue(item?.originalItem?.netWeight);
    const breakdownWeight = numberValue(item?.usedWeight);
    const totalWeight =
      breakdownWeight > 0 ? breakdownWeight : originalNetWeight;
    const lockDetails = (item?.details || []).filter(
      (detail) => detail?.isLockedPrice || detail?.priceSource === "lock",
    );
    const extraDetail = (item?.details || []).find(
      (detail) =>
        !detail?.isLockedPrice &&
        detail?.priceSource !== "lock" &&
        detail?.priceSource !== "po" &&
        numberValue(detail?.usedWeight) > 0,
    );
    const selectedLockIds = new Set(
      (item?.details || [])
        .filter(
          (detail) =>
            detail?.isLockedPrice || detail?.priceSource === "lock",
        )
        .map((detail) => numberValue(detail?.lockId ?? detail?.lockInfo?.id))
        .filter(Boolean),
    );
    const sourceLocks = selectedLockIds.size
      ? lockItems.filter((lock) => selectedLockIds.has(numberValue(lock?.id)))
      : lockItems.length
        ? lockItems
        : item?.originalItem?.lockPricings || [];
    const locks = sourceLocks
      .filter((lock) => numberValue(lock.sellProductId ?? lock.productId) === numberValue(item?.productId))
      .filter(
        (lock) =>
          String(lock?.status || "OPEN").toUpperCase() === "OPEN" &&
          String(lock?.master?.status || "APPROVED").toUpperCase() ===
            "APPROVED",
      )
      .slice()
      .sort(
        (a, b) =>
          new Date(a.recordDate || 0).getTime() -
          new Date(b.recordDate || 0).getTime(),
      );

    const rows = [];
    // ใช้น้ำหนักที่ถูกจัดสรรให้รายการนี้แล้วเป็นหลัก ไม่ใช้ remainingWeight
    // ของ LockPricing เพราะค่านั้นอาจถูกหักจากรายการอื่นไปแล้ว
    if (lockDetails.length > 0) {
      lockDetails.forEach((detail) => {
        const lock = locks.find(
          (candidate) =>
            numberValue(candidate?.id) ===
            numberValue(detail?.lockId ?? detail?.lockInfo?.id),
        );
        const weight = numberValue(detail?.usedWeight);
        if (weight <= 0) return;
        const price = numberValue(detail?.pricePerUnit ?? lock?.specialPrice);
        rows.push({
          type: "insurance",
          date: detail?.lockInfo?.recordDate ?? lock?.recordDate,
          weight,
          price,
          total: weight * price,
        });
      });
    } else {
      let remainingFromLocks = totalWeight;
      locks.forEach((lock) => {
        if (remainingFromLocks <= 0) return;
        const availableWeight = numberValue(lock.remainingWeight ?? lock.weight);
        const usedWeight = Math.min(remainingFromLocks, availableWeight);
        if (usedWeight <= 0) return;

        const price = numberValue(lock.specialPrice);
        rows.push({
          type: "insurance",
          date: lock.recordDate,
          weight: usedWeight,
          price,
          total: usedWeight * price,
        });
        remainingFromLocks -= usedWeight;
      });
    }

    const protectedWeightFromRows = rows
      .filter((row) => row.type === "insurance")
      .reduce((sum, row) => sum + row.weight, 0);
    // น้ำหนักส่วนเกินต้องเป็นส่วนต่างของน้ำหนักสินค้ากับน้ำหนักที่ Lock ใช้จริง
    const remainingWeight = Math.max(0, totalWeight - protectedWeightFromRows);

    const source = extraDetail || item?.details?.[0] || {};
    const basePrice = numberValue(
      source.basePrice ?? item?.originalItem?.product?.price,
    );
    const groupPrice = numberValue(
      source.groupPrice ?? source.pricePerUnit ?? basePrice,
    );
    const persistedExtraPrice = item?.originalItem?.insuranceExtraPrice;
    const customInputPrice =
      customPrice === "" && persistedExtraPrice != null
        ? persistedExtraPrice
        : customPrice;
    const extraPrice =
      mode === "custom"
        ? numberValue(customInputPrice)
        : mode === "group"
          ? groupPrice
          : basePrice;

    if (remainingWeight > 0.004) {
      rows.push({
        type: "extra",
        weight: remainingWeight,
        price: extraPrice,
        total: remainingWeight * extraPrice,
      });
    }

    return {
      rows,
      totalWeight,
      totalPrice: rows.reduce((sum, row) => sum + row.total, 0),
      basePrice,
      groupPrice,
    };
  }, [customPrice, item, lockItems, mode]);

  const protectedWeight = details.rows
    .filter((row) => row.type === "insurance")
    .reduce((sum, row) => sum + row.weight, 0);
  const extra = details.rows.find((row) => row.type === "extra");

  const handleSave = () => {
    const extraPrice = extra?.price ?? details.groupPrice;
    onSave?.({
      pricePerUnit: extraPrice,
      totalWeight: details.totalWeight,
      priceMode: mode,
      protectedWeight,
      extraWeight: extra?.weight ?? 0,
      protectedTotal: details.rows
        .filter((row) => row.type === "insurance")
        .reduce((sum, row) => sum + row.total, 0),
    });
  };

  if (!item) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: { borderRadius: "16px" },
        className: "shadow-2xl overflow-hidden",
      }}
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white">
        <div>
          <h2 className="text-xl font-black text-slate-800 tracking-tight">
            {item.productName || item.originalItem?.productName || "-"}
          </h2>
          <p className="text-sm text-slate-500 font-medium mt-0.5">
            รายละเอียดประกันราคา
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-all"
          aria-label="ปิดรายละเอียดประกันราคา"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <DialogContent className="!p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHead>
              <TableRow className="bg-gray-50 border-b border-gray-100">
                <TableCell className="!py-4 !px-6 !text-xs !font-bold !text-slate-500 !uppercase !tracking-wider">
                  ประเภทราคา
                </TableCell>
                <TableCell align="right" className="!py-4 !px-6 !text-xs !font-bold !text-slate-500 !uppercase !tracking-wider">
                  น้ำหนักซื้อจริง
                </TableCell>
                <TableCell align="right" className="!py-4 !px-6 !text-xs !font-bold !text-slate-500 !uppercase !tracking-wider">
                  ราคาซื้อ
                </TableCell>
                <TableCell align="right" className="!py-4 !px-6 !text-xs !font-bold !text-slate-500 !uppercase !tracking-wider">
                  ราคารวม
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {details.rows.map((row, index) => (
                <TableRow
                  key={`${row.type}-${row.date || "extra"}-${index}`}
                  className="hover:bg-blue-50/30 transition-colors border-b border-gray-50 last:border-0"
                >
                  <TableCell className="!py-4 !px-6 !text-sm !font-bold !text-slate-700">
                    {row.type === "insurance" ? (
                      <>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-purple-50 text-purple-700 text-xs font-bold border border-purple-100">
                          ประกันราคา
                        </span>
                        <div className="mt-1 text-xs font-medium text-slate-500">
                          วันที่ {row.date ? new Date(row.date).toLocaleDateString("th-TH") : "-"}
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 text-xs font-bold border border-amber-100">
                          น้ำหนักส่วนเกิน (
                          {mode === "base"
                            ? "ราคาพื้นฐาน"
                            : mode === "group"
                              ? "ราคากลุ่ม"
                              : "ราคากำหนดเอง"}
                          )
                        </span>
                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={() => setOpenExtraPriceDialog(true)}
                            className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-black text-amber-700 hover:bg-amber-100 transition-colors"
                          >
                            เลือกราคา
                          </button>
                        </div>
                      </>
                    )}
                  </TableCell>
                  <TableCell align="right" className="!py-4 !px-6 !text-sm !font-bold !text-slate-700">
                    {formatNumber(row.weight)}
                  </TableCell>
                  <TableCell align="right" className="!py-4 !px-6 !text-sm !font-bold !text-slate-600">
                    {formatNumber(row.price)}
                  </TableCell>
                  <TableCell align="right" className="!py-4 !px-6 !text-sm !font-black !text-slate-800">
                    {formatNumber(row.total)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-slate-50/80">
                <TableCell className="!py-4 !px-6 !text-sm !font-black !text-slate-800">
                  รวม
                </TableCell>
                <TableCell align="right" className="!py-4 !px-6 !text-sm !font-black !text-emerald-600">
                  {formatNumber(details.totalWeight)}
                </TableCell>
                <TableCell />
                <TableCell align="right" className="!py-4 !px-6 !text-base !font-black !text-blue-600">
                  {formatNumber(details.totalPrice)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

      </DialogContent>

      <DialogActions className="!px-6 !py-4 border-t border-gray-100 bg-white">
        <Button onClick={onClose}>ยกเลิก</Button>
        <Button variant="contained" onClick={handleSave}>
          บันทึก
        </Button>
      </DialogActions>

      <Dialog
        open={openExtraPriceDialog}
        onClose={() => setOpenExtraPriceDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          เลือกราคาส่วนเกินประกันราคา
        </DialogTitle>
        <DialogContent dividers>
          {extra && (
            <Stack spacing={2.5}>
              <Box>
                <Typography sx={{ fontWeight: 800, color: "text.secondary" }}>
                  สินค้า
                </Typography>
                <Typography sx={{ fontWeight: 900, fontSize: 18 }}>
                  {item.productName || item.originalItem?.productName || "-"}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1.5,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "grey.50",
                  border: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                <Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    น้ำหนักประกันราคา
                  </Typography>
                  <Typography sx={{ fontWeight: 900 }}>
                    {formatNumber(protectedWeight)} กก.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    น้ำหนักส่วนเกิน
                  </Typography>
                  <Typography sx={{ fontWeight: 900 }}>
                    {formatNumber(extra.weight)} กก.
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <RadioGroup
                value={mode}
                onChange={(event) => setMode(event.target.value)}
              >
                <FormControlLabel
                  value="base"
                  control={<Radio />}
                  label={
                    <Box className="flex items-center justify-between gap-4 w-full">
                      <span className="font-bold">ราคาพื้นฐาน</span>
                      <span className="font-black text-slate-700">
                        {formatNumber(details.basePrice)} บ./กก.
                      </span>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", mx: 0, py: 0.5 }}
                />
                <FormControlLabel
                  value="group"
                  control={<Radio />}
                  label={
                    <Box className="flex items-center justify-between gap-4 w-full">
                      <span className="font-bold">ราคากลุ่ม</span>
                      <span className="font-black text-slate-700">
                        {formatNumber(details.groupPrice)} บ./กก.
                      </span>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", mx: 0, py: 0.5 }}
                />
                <FormControlLabel
                  value="custom"
                  control={<Radio />}
                  label={
                    <Box className="flex items-center justify-between gap-4 w-full">
                      <span className="font-bold">ราคากำหนดเอง</span>
                      <span className="font-black text-slate-700">
                        {formatNumber(numberValue(customPrice || extra.price))} บ./กก.
                      </span>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", mx: 0, py: 0.5 }}
                />
              </RadioGroup>

              {mode === "custom" && (
                <TextField
                  label="กรอกราคากำหนดเอง"
                  value={customPrice}
                  onChange={(event) => setCustomPrice(event.target.value)}
                  type="number"
                  fullWidth
                  inputProps={{ step: "0.01", min: "0" }}
                />
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenExtraPriceDialog(false)}>ยกเลิก</Button>
          <Button
            variant="contained"
            onClick={() => setOpenExtraPriceDialog(false)}
          >
            บันทึก
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
